import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import { ChevronLeftRounded as ChevronLeftRoundedIcon, StorefrontRounded as StorefrontRoundedIcon } from "../icons";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { T } from "./Translate";
import { pageContentSx } from "../pageLayout";
import { brandColors, barColors } from "../theme";

const QRPaymentProcessor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [scannedQR, setScannedQR] = useState(null);
  const [storeName, setStoreName] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const headerBar = (
    <AppBar position="fixed" sx={{ bgcolor: barColors.background, color: barColors.text }}>
      <Toolbar sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", maxWidth: { xs: "100%", md: "980px" }, mx: "auto" }}>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ChevronLeftRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2 }}>
            <T>Confirmar Pago</T>
          </Typography>
        </Box>
      </Toolbar>
    </AppBar>
  );

  useEffect(() => {
    const fetchStoreDetails = async () => {
      try {
        if (!location.state?.couponData) {
          navigate("/home");
          return;
        }

        const qrData = location.state.couponData;
        setScannedQR(qrData);

        try {
          const merchantDoc = await getDoc(doc(db, "users", qrData.merchantId));
          if (merchantDoc.exists()) {
            setStoreName(merchantDoc.data().displayName || "Comercio");
          } else {
            setStoreName("Comercio");
          }
        } catch (err) {
          console.error("Error fetching merchant details:", err);
          setStoreName(qrData.storeName || "Comercio");
        }
      } catch (err) {
        console.error("Error processing QR data:", err);
        setError(<T>Error al procesar los datos del QR</T>);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoreDetails();
  }, [location, navigate]);

  const handlePayment = () => {
    if (!scannedQR) {
      setError(<T>No se encontraron datos de pago</T>);
      return;
    }

    navigate("/checkout", {
      state: {
        couponData: {
          ...scannedQR,
          storeName: storeName,
        },
      },
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth={false} disableGutters sx={pageContentSx}>
        {headerBar}
        <Container maxWidth="sm" sx={{ pt: 12, pb: 4, textAlign: "center" }}>
          <CircularProgress sx={{ color: brandColors.accent, mt: 6 }} />
          <Typography sx={{ mt: 2, color: brandColors.textSecondary }}>
            <T>Cargando datos de pago...</T>
          </Typography>
        </Container>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      {headerBar}

      <Container maxWidth="sm" sx={{ pt: 12, pb: 4, mb: 10 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            backgroundColor: brandColors.backgroundPaper,
            border: `1px solid ${brandColors.divider}`,
          }}
        >
          {scannedQR && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                <T>Resumen del Pago</T>
              </Typography>
              <Divider sx={{ my: 2, borderColor: brandColors.divider }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <StorefrontRoundedIcon sx={{ color: brandColors.accent }} fontSize="small" />
                <Typography variant="body1">
                  <strong><T>Comercio:</T></strong> {storeName}
                </Typography>
              </Box>

              {Array.isArray(scannedQR?.offers) && scannedQR.offers.length > 0 ? (
                scannedQR.offers.map((offer, index) => {
                  return (
                    <Card
                      key={index}
                      elevation={0}
                      sx={{
                        mt: 2,
                        backgroundColor: brandColors.backgroundDefault,
                        border: `1px solid ${brandColors.divider}`,
                        borderRadius: 2,
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                          {typeof offer.title === 'string'
                            ? (offer.title.length > 50
                                ? `${offer.title.substring(0, 50)}...`
                                : offer.title)
                            : <T>Producto sin nombre</T>}
                        </Typography>
                        <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                          {typeof offer.description === 'string'
                            ? (offer.description.length > 100
                                ? `${offer.description.substring(0, 100)}...`
                                : offer.description)
                            : <T>Sin descripción</T>}
                        </Typography>
                        <Box
                          sx={{
                            mt: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                            <T>Precio original: ${typeof offer.price === 'number'
                              ? offer.price.toFixed(2)
                              : parseFloat(offer.price || 0).toFixed(2)}</T>
                          </Typography>
                          {offer.offer > 0 && (
                            <Chip
                              size="small"
                              label={<T>{offer.offer}% OFF</T>}
                              sx={{ backgroundColor: "success.main", color: "white", fontWeight: "bold" }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                  <T>No hay ofertas disponibles</T>
                </Typography>
              )}

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  background: `linear-gradient(135deg, ${brandColors.primaryHover} 0%, ${brandColors.primary} 100%)`,
                  color: "white",
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.85, mb: 0.5 }}>
                  <T>Total a pagar</T>
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  ${typeof scannedQR.totalAmount === 'number'
                    ? scannedQR.totalAmount.toFixed(2)
                    : parseFloat(scannedQR.totalAmount || 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3, borderColor: brandColors.divider }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={handlePayment}
            disabled={!scannedQR}
            sx={{
              mt: 1,
              backgroundColor: brandColors.action,
              "&:hover": {
                backgroundColor: brandColors.actionHover,
              },
              height: 48,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            <T>Pagar</T>
          </Button>
        </Paper>
      </Container>
    </Container>
  );
};

export default QRPaymentProcessor;